import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Center,
  Checkbox,
  Code,
  Group,
  Stack,
  Table,
  Tabs,
  Text,
  Title,
} from '@mantine/core';
import { IconAlertTriangle, IconDownload } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  openapiDownloadUrl,
  postmanDownloadUrl,
  postmanEnvironmentDownloadUrl,
  useIntegration,
} from '../api/integrations';
import { useMe } from '../api/session';
import { CustomFieldsPanel } from './integration/custom-fields-panel';
import { CredentialsCard } from './integration-credentials';

const SCOPE_GROUPS_HINT =
  'Choose the scopes your integration needs. The Postman collection + endpoint catalogue below stay in sync with what you select. Scopes are enforced server-side regardless of what you tick — selection here just keeps the operator UI focused.';

export function IntegrationPage() {
  const { orgId = '', productId = '' } = useParams<{ orgId: string; productId: string }>();
  const query = useIntegration(orgId, productId);
  const me = useMe();
  const isAdmin = me.data?.role === 'admin';

  const allScopes = useMemo(
    () => Array.from(new Set((query.data?.catalogue ?? []).map((e) => e.scope))).sort(),
    [query.data?.catalogue],
  );

  // ponytail: localStorage — UI view preference, not server-enforced auth; survives reload without a DB table
  const storageKey = `integration-scopes-${orgId}-${productId}`;
  const [selectedScopes, setSelectedScopes] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved) as string[];
        if (parsed.length > 0) return new Set(parsed);
      }
    } catch {}
    return new Set();
  });

  // First load: select everything by default so the catalogue shows
  // the full available surface before the operator narrows down.
  if (selectedScopes.size === 0 && allScopes.length > 0) {
    setSelectedScopes(new Set(allScopes));
    try {
      localStorage.setItem(storageKey, JSON.stringify(allScopes));
    } catch {}
  }

  if (query.isPending) {
    return (
      <Center mih={240}>
        <Text c="dimmed">Loading…</Text>
      </Center>
    );
  }
  if (query.isError || !query.data) {
    return (
      <Alert color="red" icon={<IconAlertTriangle size={18} />}>
        Could not load integration details.
      </Alert>
    );
  }
  const { orgName, productCode, productDisplayName, baseUrl, authBaseUrl, clientId, catalogue } =
    query.data;

  // SDK constructor takes the platform host root (no path prefix); strip
  // the per-service suffix off the displayed `baseUrl` for the snippet
  // below. Best-effort — if the URL parses as absolute, use its origin;
  // otherwise fall back to the raw value so the snippet still renders.
  let platformBaseUrl = baseUrl;
  try {
    platformBaseUrl = new URL(baseUrl).origin;
  } catch {
    // ignore, keep baseUrl as-is
  }

  const visibleCatalogue = catalogue.filter((e) => selectedScopes.has(e.scope));
  const groupedByCategory = visibleCatalogue.reduce<Record<string, typeof catalogue>>((acc, e) => {
    (acc[e.category] ??= []).push(e);
    return acc;
  }, {});

  const downloadUrl = postmanDownloadUrl(orgId, productId, Array.from(selectedScopes));
  const environmentDownloadUrl = postmanEnvironmentDownloadUrl(orgId, productId);

  function toggleScope(scope: string) {
    setSelectedScopes((prev) => {
      const next = new Set(prev);
      if (next.has(scope)) next.delete(scope);
      else next.add(scope);
      try {
        localStorage.setItem(storageKey, JSON.stringify([...next]));
      } catch {}
      return next;
    });
  }

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end">
        <div>
          <Title order={2}>Integrate with {productDisplayName}</Title>
          <Text c="dimmed" size="sm">
            {orgName} — <Code>{productCode}</Code>
          </Text>
        </div>
        <Button component={Link} variant="subtle" to={`/orgs/${orgId}/products`}>
          ← back to products
        </Button>
      </Group>

      <CredentialsCard
        orgId={orgId}
        productId={productId}
        catalogueScopes={allScopes}
        productDisplayName={productDisplayName}
      />

      <Card withBorder padding="lg">
        <Stack gap="sm">
          <Title order={5}>Endpoint scopes</Title>
          <Text size="sm" c="dimmed">
            {SCOPE_GROUPS_HINT}
          </Text>
          <Group gap="xs" wrap="wrap">
            {allScopes.map((s) => (
              <Checkbox
                key={s}
                label={<Code>{s}</Code>}
                checked={selectedScopes.has(s)}
                onChange={() => toggleScope(s)}
              />
            ))}
          </Group>
        </Stack>
      </Card>

      <Tabs defaultValue="direct">
        <Tabs.List>
          <Tabs.Tab value="direct">Direct API</Tabs.Tab>
          <Tabs.Tab value="sdk">SDK (TypeScript)</Tabs.Tab>
          <Tabs.Tab value="postman">Postman</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="direct" pt="md">
          <Card withBorder padding="lg">
            <Stack gap="sm">
              <Title order={5}>Direct API</Title>
              <Text size="sm" c="dimmed">
                Your integration drives the flow — call any of the endpoints below with an OAuth2
                access token obtained via the client-credentials grant. Token TTL is short-lived
                (~1h); re-issue on demand or at expiry.
              </Text>

              <Group gap="md" wrap="wrap">
                <KeyValue label="API base URL" value={baseUrl} />
                <KeyValue label="Auth base URL" value={authBaseUrl} />
                <KeyValue label="Client id" value={clientId ?? '— provision via admin tools'} />
              </Group>

              <Text size="sm" fw={600}>
                Token request
              </Text>
              <Code block>
                {`POST ${authBaseUrl}/oauth/token
Content-Type: application/x-www-form-urlencoded

grant_type=client_credentials
client_id=<your-client-id>
client_secret=<your-client-secret>
scope=${Array.from(selectedScopes).join(' ') || '<scopes>'}`}
              </Code>

              <Text size="sm" fw={600}>
                Calling an endpoint
              </Text>
              <Code block>
                {`curl -X POST ${baseUrl}/cases \\
  -H "Authorization: Bearer <access-token>" \\
  -H "Content-Type: application/json" \\
  -d '{"patient_id": "<patient-uuid>"}'`}
              </Code>
            </Stack>
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="sdk" pt="md">
          <Card withBorder padding="lg">
            <Stack gap="sm">
              <Title order={5}>SDK</Title>
              <Text size="sm" c="dimmed">
                The TypeScript SDK wraps the OAuth2 token flow and provides typed methods for the
                public endpoints. Calls outside your granted scopes return 403 from the platform —
                the SDK doesn&apos;t pre-filter, so you can develop against the full surface while
                operations narrows scopes.
              </Text>

              <Text size="sm" fw={600}>
                Install
              </Text>
              <Code block>{`npm install @sa-platform/client-sdk`}</Code>

              <Text size="sm" fw={600}>
                Use
              </Text>
              <Code block>
                {`import { SkinAnalyticsClient } from '@sa-platform/client-sdk';

const client = new SkinAnalyticsClient({
  baseUrl: '${platformBaseUrl}', // platform host root; SDK appends per-service prefixes
  clientId: process.env.SA_CLIENT_ID!,
  clientSecret: process.env.SA_CLIENT_SECRET!,
  scopes: ${JSON.stringify(Array.from(selectedScopes))},
});

const patient = await client.patients.create({
  given_name: 'Alice',
  family_name: 'Anderson',
  dob: '1985-06-15',
});
const c = await client.cases.create({ patient_id: patient.id as string });`}
              </Code>

              <Text size="sm" c="dimmed">
                Full method list:{' '}
                <Anchor
                  href="https://github.com/Skin-Analytics/clinical-data-model/blob/main/packages/client-sdk/README.md"
                  target="_blank"
                >
                  README on GitHub
                </Anchor>
              </Text>
            </Stack>
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="postman" pt="md">
          <Card withBorder padding="lg">
            <Stack gap="sm">
              <Title order={5}>Postman collection</Title>
              <Text size="sm" c="dimmed">
                Download a Postman v2.1 collection scoped to the ticked endpoints above + a matching{' '}
                <strong>environment</strong> with <Code>baseUrl</Code> (the platform host root) and{' '}
                <Code>clientId</Code> pre-filled. Import both files in Postman, paste the{' '}
                <Code>client_secret</Code> into the environment variable (we never ship secrets),
                and run <Code>0. Auth</Code> to mint a token.
              </Text>
              <Group>
                <Button
                  component="a"
                  href={downloadUrl}
                  download
                  leftSection={<IconDownload size={16} />}
                  disabled={selectedScopes.size === 0}
                >
                  Download Postman collection
                </Button>
                <Button
                  component="a"
                  href={environmentDownloadUrl}
                  download
                  variant="default"
                  leftSection={<IconDownload size={16} />}
                >
                  Download environment
                </Button>
                <Button
                  component="a"
                  href={openapiDownloadUrl(orgId, productId)}
                  download="openapi.json"
                  variant="default"
                  leftSection={<IconDownload size={16} />}
                >
                  Download OpenAPI
                </Button>
                <Badge variant="light" color={selectedScopes.size === 0 ? 'red' : 'green'}>
                  {selectedScopes.size} scope{selectedScopes.size === 1 ? '' : 's'} selected
                </Badge>
              </Group>
            </Stack>
          </Card>
        </Tabs.Panel>
      </Tabs>

      <Card withBorder padding="lg">
        <Stack gap="sm">
          <Title order={5}>Endpoint catalogue</Title>
          <Text size="sm" c="dimmed">
            Endpoints visible below match the scopes ticked above. Each row maps a route to the
            scope your client must hold. Workflows endpoints appear when the
            <Code>orchestrator:*</Code> scopes are selected.
          </Text>

          {visibleCatalogue.some((e) => e.category === 'Images') && (
            <Alert variant="light" color="blue" title="Image upload uses a two-step S3 flow">
              Image bytes upload <strong>directly to S3</strong> via a presigned URL returned by{' '}
              <Code>POST /v1/clinical-api/images:initiate</Code>. After PUT-ing the bytes to the
              presigned URL, attach the image to a finding with{' '}
              <Code>POST /v1/clinical-api/findings/:findingId/images/:imageId</Code>. See the{' '}
              <strong>Images</strong> rows below.
            </Alert>
          )}

          {Object.keys(groupedByCategory).length === 0 ? (
            <Text c="dimmed" size="sm">
              No endpoints match the current scope selection.
            </Text>
          ) : (
            Object.entries(groupedByCategory).map(([category, entries]) => (
              <div key={category}>
                <Title order={6} mt="sm" mb="xs">
                  {category}
                </Title>
                <Table withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 80 }}>Method</Table.Th>
                      <Table.Th>Path</Table.Th>
                      <Table.Th>Scope</Table.Th>
                      <Table.Th>Description</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {entries.flatMap((e) => {
                      const isCasesWrite =
                        e.method === 'POST' && e.path === '/v1/clinical-api/cases';
                      return [
                        <Table.Tr key={`${e.method}-${e.path}`}>
                          <Table.Td>
                            <Badge variant="light">{e.method}</Badge>
                          </Table.Td>
                          <Table.Td>
                            <Code>{e.path}</Code>
                          </Table.Td>
                          <Table.Td>
                            <Code>{e.scope}</Code>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{e.description}</Text>
                          </Table.Td>
                        </Table.Tr>,
                        ...(isCasesWrite
                          ? [
                              <Table.Tr key={`${e.method}-${e.path}-custom-fields`}>
                                <Table.Td
                                  colSpan={4}
                                  style={{ background: 'var(--mantine-color-gray-0)' }}
                                >
                                  <CustomFieldsPanel
                                    orgId={orgId}
                                    productId={productId}
                                    readOnly={!isAdmin}
                                  />
                                </Table.Td>
                              </Table.Tr>,
                            ]
                          : []),
                      ];
                    })}
                  </Table.Tbody>
                </Table>
              </div>
            ))
          )}
        </Stack>
      </Card>
    </Stack>
  );
}

function KeyValue({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Code>{value}</Code>
    </div>
  );
}
